import { type ImageThumbnail } from '@/types/schema/fields';
import { type KnackField } from '@/types/schema/KnackField';
import { type DetailsView, type DetailsViewInput } from '@/types/schema/views/DetailsView';
import { type ListView } from '@/types/schema/views/ListView';
import { type MapView } from '@/types/schema/views/MapView';
import { type SearchListView } from '@/types/schema/views/SearchView';
import { type FormattedDetailsDisplayRuleAction } from '@/components/views/details/useDetailsDisplayRules';

export function getLabelStyleClassNames(
  input: DetailsViewInput,
  view: DetailsView | ListView | MapView | SearchListView
) {
  const isDefaultLabelFormat = !input.format?.label_custom;

  if (isDefaultLabelFormat && view.type !== 'map' && view.type !== 'search') {
    return {
      'text-left': view.label_format === 'left',
      'text-right': view.label_format === 'right'
    };
  }

  return {
    'text-right': input.format?.label_format === 'right',
    'text-left': input.format?.label_format === 'left'
  };
}

export function getValueStylesClassNames(
  input: DetailsViewInput,
  displayRules: FormattedDetailsDisplayRuleAction
) {
  return {
    'text-4xl': input.format?.styles?.includes('h1'),
    'text-2xl': input.format?.styles?.includes('h2'),
    'font-bold': input.format?.styles?.includes('strong') || displayRules.valueStyles.bold,
    italic: input.format?.styles?.includes('em') || displayRules.valueStyles.italic,
    'line-through': displayRules.valueStyles.strikethrough
  };
}

function getThumbName(thumb: ImageThumbnail): string {
  if (thumb.thumb_type === 'percentage') {
    return `${thumb.percentage}%`;
  }

  if (thumb.thumb_type === 'square') {
    return `${thumb.length}px square`;
  }

  return `${thumb.width}x${thumb.height}`;
}

export function getFieldsWithThumbs(fields: KnackField[] | null): KnackField[] {
  if (!fields) {
    return [];
  }

  const fieldsWithThumbs: KnackField[] = [];

  // Find all image fields and add their thumbnails
  fields.forEach((field) => {
    fieldsWithThumbs.push(field);
    if (field.type === 'image' && field.format?.thumbs?.length) {
      // Add each thumbnail as a separate field
      field.format.thumbs.forEach((thumb) => {
        if (!thumb.key) {
          return;
        }

        fieldsWithThumbs.push({
          ...field,
          key: `${field.key}:${thumb.key}`,
          name: `${field.name} - ${getThumbName(thumb)}`,
          type: field.type,
          thumbKey: thumb.key
        });
      });
    }
  });

  return fieldsWithThumbs;
}
